export function buildRefs(el, multiple = false, customRefs = {}) {
  const QS = multiple ? 'querySelectorAll' : 'querySelector'
  const DA = multiple ? 'data-refs' : 'data-ref'
  return new Proxy(
    {},
    {
      get(target, prop) {
        if (!target[prop]) {
          const selector = customRefs[prop] ?? `[${DA}="${prop}"]`
          target[prop] = el[QS](selector)
          if (!target[prop]) {
            // eslint-disable-next-line no-undef
            if (process.env.NODE_ENV !== 'production') {
              console.warn(`ref ${prop} not found.`)
            }
          }
        }
        return target[prop]
      },
    }
  )
}

export function getJSON(
  node,
  selector = 'script[type="application/json"]',
  property = 'textContent'
) {
  let data = {}
  try {
    data = JSON.parse(node.querySelector(selector)[property])
  } catch (e) {
    /* empty */
  }
  return data
}

/**
 * Trap focus on element. This can improve the accessibility with Popups/overlays (A11Y.
 * @param {HTMLElement} el Any HTML Element
 * @param {boolean=} bindFirst If `false` will not bind event listeners on {@link first}
 * @param {boolean=} bindLast If `false` will not bind event listeners on {@link last}
 * @see {@link https://gomakethings.com/how-to-get-the-first-and-last-focusable-elements-in-the-dom/}
 * @example
 *   trapFocus(document.createElement('div'))
 *   trapFocus(document.createElement('div'), false, true)
 * @returns {void}
 */
export const trapFocus = (el, bindFirst, bindLast) => {
  if (!el) throw Error("Missing required argument 'el'")
  if (el && !(el instanceof window.Element)) {
    throw TypeError(
      `Expected argument 'el' to be an instance of 'Element' but was '${el.constructor.name}'`
    )
  }

  /** Elements in {@link el} that can be focussed */
  const elements = el.querySelectorAll(
    'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  /** @type {Element} The first focussable element in {@link el} */
  let first

  /** @type {Element} The last focussable element in {@link el} */
  let last

  if (elements && elements.length) {
    first = elements[0]
    last = elements[elements.length - 1]
  }

  /**
   * On keydown on {@link first}
   * @param {KeyboardEvent} e Keyboard event
   * @returns {void}
   */
  const onKeyDownFirst = e => {
    if (e.code === 'Tab' && e.shiftKey) {
      e.preventDefault()
      last.focus()
    }
  }

  /**
   * On keydown on {@link last}
   * @param {KeyboardEvent} e Keyboard event
   * @returns {void}
   */
  const onKeyDownLast = e => {
    if (e.code === 'Tab') {
      if (!e.shiftKey) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  if (first || last) {
    if (bindFirst !== false) first.addEventListener('keydown', onKeyDownFirst)
    if (bindLast !== false) last.addEventListener('keydown', onKeyDownLast)
  }
}

/**
 * Detect click outside of an element
 * @param {HTMLElement} el Any element
 * @param {() => void=} callback Callback function
 * @returns {() => void} Function that removes all events listeners that were added by this function
 * @example
 *   const removeOnClickOutside = onClickOutside(document.getElementById('my-overlay'), () => {
 *     console.log('you clicked outside of the element')
 *     removeOnClickOutside() // not listening anymore (optional)
 *   }) // now listening
 */
export const onClickOutside = (el, callback) => {
  /**
   * Clicking on the {@link el} or any child will do nothing
   * @param {MouseEvent} e Click event
   */
  const clickInside = e => e.stopPropagation()

  /**
   * Clicking outside of the {@link el} will call the callback function and remove the event listeners attached
   */
  const clickOutside = () => {
    if (callback) callback.call(this)
    el.removeEventListener('mouseup', clickInside)
  }

  document.addEventListener('mouseup', clickOutside, { once: true })
  el.addEventListener('mouseup', clickInside)

  /**
   * Remove all event listeners
   */
  const cleanup = () => el.removeEventListener('mouseup', clickInside)

  return cleanup
}

export const styleFileUpload = el => {
  const inputFileUploads = el.querySelectorAll('input[type="file"]')
  inputFileUploads.forEach(input => {
    const inputID = input.id
    const label = el.querySelector(`label[for="${inputID}"]`)
    const inputParent = input.parentNode

    inputParent.insertBefore(label, inputParent.firstChild)
    input.parentNode.classList.add('file-upload')
  })
}

export const handleUploadInfos = el => {
  const inputFieldUploads = el.querySelectorAll('input[type="file"]')
  inputFieldUploads.forEach(input => {
    const inputParent = input.parentNode
    // If a file is selected to upload, show the file name next to the label (the input field is hidden)
    input.addEventListener('change', e => {
      const selectedFile = document.createElement('span')
      selectedFile.classList.add('selected-file')
      selectedFile.innerHTML = e.target.files[0].name
      inputParent.appendChild(selectedFile)

      // If there is a file append a remove button
      const removeButton = document.createElement('button')
      removeButton.classList.add('remove-file')
      removeButton.setAttribute('type', 'button')
      removeButton.innerHTML = 'Datei entfernen'
      inputParent.appendChild(removeButton)
      removeButton.addEventListener(
        'click',
        () => {
          input.value = ''
          selectedFile.remove()
          removeButton.remove()
        },
        { once: true }
      )
    })
  })
}

/**
 * Reads the value of a CSS variable from a given element or the root.
 * @param {string} varName - The name of the CSS variable to read.
 * @param {HTMLElement=} el - The element to read the CSS variable from. Defaults to the root.
 * @param removeUnit {boolean=} - Remove unit from value
 * @returns {string} The value of the CSS variable.
 * @example
 *   const bgColor = getCSSVariable('--background-color');
 *   console.log(bgColor); // Outputs the value of --background-color from the root
 *   const bgColorFromElement = getCSSVariable('--background-color', document.querySelector('.my-class'));
 *   console.log(bgColorFromElement); // Outputs the value of --background-color from the specified element
 */
export function getCSSVariable(
  varName,
  el = document.documentElement,
  removeUnit = false
) {
  const removeCSSUnit = value => {
    const units = [
      'px',
      'em',
      '%',
      'rem',
      'vh',
      'vw',
      'vmin',
      'vmax',
      'pt',
      'cm',
      'mm',
      'in',
      'pc',
      'ex',
      'ch',
    ]
    for (const unit of units) {
      if (value.endsWith(unit)) {
        return value.replace(unit, '')
      }
    }
    return value
  }

  if (!varName) throw new Error("Missing required argument 'varName'")
  if (typeof varName !== 'string') {
    throw new TypeError("Expected 'varName' to be a string")
  }
  if (el && !(el instanceof window.Element)) {
    throw new TypeError(
      `Expected 'el' to be an instance of 'Element' but was '${el ? el.constructor.name : 'undefined'}'`
    )
  }
  /* getComputedStyle {@type CSSStyleDeclaration} */
  const style = window.getComputedStyle(el)
  let value = style.getPropertyValue(varName).trim()

  if (removeUnit) {
    value = removeCSSUnit(value)
  }

  return value
}

/**
 * Initializes a MutationObserver on a target element with custom logic.
 * @param {HTMLElement} targetElement - The target element to observe for mutations.
 * @param {MutationObserverInit} observerOptions - Options for the MutationObserver.
 * @param {(mutations: MutationRecord[], observer: MutationObserver) => void} callback - Custom logic to execute when mutations are observed.
 * @returns {MutationObserver} - The initialized MutationObserver.
 * @example
 *   initializeObserver(
 *     document.getElementById('myElement'),
 *     { attributes: true },
 *     (mutations, observer) => {
 *       // Custom logic here
 *     }
 *   );
 */

export function mutationObserver(targetElement, observerOptions, callback) {
  if (!targetElement || !(targetElement instanceof HTMLElement)) {
    throw new Error('Invalid target element for MutationObserver.')
  }
  if (typeof callback !== 'function') {
    throw new Error('Callback must be a function.')
  }

  const observer = new MutationObserver((mutations, observer) => {
    callback(mutations, observer)
  })

  observer.observe(targetElement, observerOptions)
  return observer
}

/**
 * Very efficient function with debounce and throttle events
 * @param {HTMLElement | Window} target Target element for eventListener
 * @param {string} eventListener EventListener
 * @param {function(*): void} func Callback function
 * @param {Number} time Wait for milliseconds after each call
 * @param {Boolean} callOnLoad Call func when this function is called
 * @returns {void}
 * @example
 *   efficientThrottleAndDebounce(window, 'scroll', () => { return console.log('hello'); }, 100, true);
 */
export function throttleAndDebounce(
  target,
  eventListener,
  func,
  time,
  callOnLoad
) {
  function throttle(f, w) {
    let t
    return function (...args) {
      if (!t) {
        f.apply(this, args)
        t = true
        setTimeout(() => (t = false), w)
      }
    }
  }

  function debounce(f, w, i) {
    let t
    return function (...args) {
      const c = this
      const later = () => {
        t = null
        if (!i) f.apply(c, args)
      }
      const n = i && !t
      clearTimeout(t)
      t = setTimeout(later, w)
      if (n) f.apply(c, args)
    }
  }

  // Call on load
  if (callOnLoad) {
    func.call()
  }

  // Throttle
  target.addEventListener(
    eventListener,
    throttle((...args) => {
      return func.apply(this, args)
    }, time)
  )

  // Debounce
  target.addEventListener(
    eventListener,
    debounce((...args) => {
      return func.apply(this, args)
    }, time)
  )
}

/**
 * Adjusts the index for the thumbs swiper to align with the main swiper in loop mode.
 * @param {Swiper} mainSwiper - The main swiper instance.
 * @param {Swiper} thumbSwiper - The thumbnail swiper instance.
 */
export const adjustThumbIndex = (mainSwiper, thumbSwiper) => {
  const realIndex = mainSwiper.realIndex
  const slidesCount = thumbSwiper.slides.length
  let thumbIndex = realIndex - 1 // Assume you want 3 slides visible with the active one in the middle.

  if (slidesCount > 3) {
    if (thumbIndex + 3 > slidesCount) {
      thumbIndex = slidesCount - 3
    }
    if (thumbIndex < 0) {
      thumbIndex = 0
    }
  } else {
    thumbIndex = realIndex
  }

  thumbSwiper.slideTo(thumbIndex, 500, false)
}

/**
 * Checks if a sticky element is currently acting sticky.
 * @param {Element} element - The element to check.
 * @returns {boolean} True if the element is sticky, false otherwise.
 */
export function isSticky(element) {
  const rect = element.getBoundingClientRect()
  return rect.top <= element.style.top.replace('px', '')
}

/**
 * Append a<script> to the DOM
 * @param {string} url Absolute URL to script
 * @param {string=} id Script id
 */
export const appendScript = (url, id) => {
  if (id && document.getElementById(id)) return
  const el = document.createElement('script')
  // defer and module
  el.setAttribute('defer', '')
  el.setAttribute('type', 'module')
  if (id) el.id = id
  el.src = url
  document.body.appendChild(el)
}

export const isMobile = () => {
  return window.matchMedia('(max-width: 700px)').matches
}

export const isTablet = () => {
  return window.matchMedia('(max-width: 1024px)').matches
}

/**
 * Find YouTube Video ID
 * @param {string} src Any YouTube url
 * @returns {string | undefined} YouTube Video ID
 */
export const YouTubeIdFromUrl = src => {
  if (!src) return

  // Split URLs like https://www.youtube.com/watch?v=dQw4w9WgXcQ by "v=" to get ID at the end
  let parts = src.split('v=')
  if (!parts || parts.length < 2) {
    // Split short-URLs like https://youtu.be/dQw4w9WgXcQ by ".be/" to get ID at the end
    parts = src.split('.be/')
  }
  if (!parts || parts.length < 2) return

  let id = parts[1]
  if (!id) return

  const ampersandPosition = id.indexOf('&')
  if (ampersandPosition !== -1) {
    id = id.substring(0, ampersandPosition)
  }

  return id
}

/**
 * Create YouTube iframe from a url
 * @param {string} url YouTube url
 * @returns {HTMLIFrameElement} YouTube iframe
 */
export const YouTubeIframeFromUrl = url => {
  const id = YouTubeIdFromUrl(url)

  if (id) {
    const el = document.createElement('iframe')
    el.src = `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1`
    // el.src = el.src + '&mute=1'
    el.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture'
    return el
  }
}

/**
 * Find Vimeo Video ID
 * @param {string} src Any Vimeo url
 * @returns {string | undefined} Vimeo Video ID
 */
export const VimeoIdFromUrl = src => {
  if (!src) return
  return src.match(
    /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/
  )[4]
}

/**
 * Create Vimeo iframe from a url
 * @param {string} url Vimeo url
 * @returns {HTMLIFrameElement} Vimeo iframe
 */
export const VimeoIframeFromUrl = url => {
  const id = VimeoIdFromUrl(url)

  if (id) {
    const el = document.createElement('iframe')
    el.src = `https://player.vimeo.com/video/${id}?dnt=1&portrait=0&autoplay=true&title=0&byline=0`
    // el.src = el.src + '&muted=1'
    el.allow = 'autoplay; fullscreen; picture-in-picture'
    return el
  }
}

/**
 * `getBoundingClientRect` without transforms. This allows you to use `transform: translateY(10px)` on an element – but get the original `DOMRect` without the transformed position. Does not work with `skew` or `rotate`.
 * @see {@link https://stackoverflow.com/a/57876601}
 * @author Adam Leggett
 * @param {HTMLElement} el Any element
 * @returns {DOMRect}
 */
export const getBoundingClientRectWithoutTransform = el => {
  const rect = el.getBoundingClientRect()
  const style = window.getComputedStyle(el)
  const tx = style.transform

  /** @type {string[]} */
  let ta

  if (tx) {
    /** @type {number} */
    let sx

    /** @type {number} */
    let sy

    /** @type {number} */
    let dx

    /** @type {number} */
    let dy

    if (tx.startsWith('matrix3d(')) {
      ta = tx.slice(9, -1).split(/, /)
      sx = +ta[0]
      sy = +ta[5]
      dx = +ta[12]
      dy = +ta[13]
    } else if (tx.startsWith('matrix(')) {
      ta = tx.slice(7, -1).split(/, /)
      sx = +ta[0]
      sy = +ta[3]
      dx = +ta[4]
      dy = +ta[5]
    } else {
      return rect
    }

    const to = style.transformOrigin
    const x = rect.x - dx - (1 - sx) * parseFloat(to)
    const y = rect.y - dy - (1 - sy) * parseFloat(to.slice(to.indexOf(' ') + 1))
    const w = sx ? rect.width / sx : el.offsetWidth
    const h = sy ? rect.height / sy : el.offsetHeight

    return {
      x,
      y,
      width: w,
      height: h,
      top: y,
      right: x + w,
      bottom: y + h,
      left: x,
    }
  } else {
    return rect
  }
}
